import mixpanel from 'mixpanel-browser'
import store from '@/store'
import * as mut from '@/store/mutations'
import router from '@/router'

let isInitialized = false

function _debugLog(...args) {
  if (process.env.NODE_ENV === 'development') {
    console.log('[DEBUG] TRACKING:', ...args)
  }
}

function _safeTrack(eventName, properties) {
  if (!isInitialized) {
    _debugLog('Tracking disabled - skipping event:', eventName, properties)
    return
  }
  mixpanel.track(eventName, properties)
}

function _safeIdentify(userId) {
  if (!isInitialized) return
  mixpanel.identify(userId)
}

function _safePeopleSet(properties) {
  if (!isInitialized) return
  mixpanel.people.set(properties)
}

function _safePeopleIncrement(property, value) {
  if (!isInitialized) return
  mixpanel.people.increment(property, value)
}

function _safeReset() {
  if (!isInitialized) return
  mixpanel.reset()
}

export function init() {
  const token = process.env.MIXPANEL_TOKEN
  const isDevelopment = process.env.NODE_ENV === 'development'

  if (token) {
    mixpanel.init(token, {
      autocapture: false,
      record_sessions_percent: 0,
      api_host: 'https://gw.pm2.io',
      ignore_dnt: true,
      debug: isDevelopment,
    })

    isInitialized = true

    if (process.env.NODE_ENV === 'development') {
      window.mixpanel = mixpanel
    }

    store.subscribe((mutation) => {
      if (mutation.type === mut.SET_USER && mutation.payload.user) {
        identifyUser(mutation.payload.user)
      }

      if (
        mutation.type === mut.SET_CRITICAL_RESOURCES_FETCHED &&
        mutation.payload.areCriticalResourcesFetched === true
      ) {
        identifyUser(store.state.user)
      }
    })

    router.afterEach((to) => {
      trackPageView(to)
    })
  } else {
    console.warn('Mixpanel token not provided - analytics disabled')
  }
}

function identifyUser(user) {
  if (!user || !user._id) return

  _safeIdentify(user._id)

  const userProperties = {
    $email: user.email,
    $phone: (user.info && user.info.phone_number) || undefined,
    $created: user.created_at,
    $first_name: user.first_name || undefined,
    $last_name: user.last_name || undefined,
    company_name: (user.info && user.info.company) || undefined,
    company_role: (user.info && user.info.company_role) || undefined,
    use_case: (user.info && user.info.use_case) || undefined,
  }

  Object.keys(userProperties).forEach(
    (key) => userProperties[key] === undefined && delete userProperties[key],
  )

  _safePeopleSet(userProperties)
}

function trackPageView(route) {
  if (!route || !route.meta) return

  const pageName = route.name
  const pageLabel = route.meta.title
  if (!pageName || !pageLabel) return

  const bucketId = route.params.id
  const currentBucket = store.getters.currentBucket
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null

  const properties = {
    page_name: pageName,
    page_label: pageLabel,
  }

  _debugLog('pageName', pageName)
  _debugLog('pageLabel', pageLabel)
  _debugLog('currentBucket', currentBucket)
  _debugLog('plan', plan)
  _debugLog('properties', properties)
  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _safeTrack('backend_page_visited', properties)
}

export function trackBucketCreated() {
  _safeTrack('bucket_created')
  _safePeopleIncrement('# of bucket created', 1)
}

export function trackTeamMemberInvited(bucketId) {
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (bucketId) {
    properties.bucket_id = bucketId
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _safeTrack('team_member_invited', properties)
}

export function trackLogout() {
  _safeReset()
}

export function trackDashboardComponentSaved() {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _safeTrack('dashboard_component_saved', properties)
}

export function trackDashboardDeleted() {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }
  _safeTrack('dashboard_deleted', properties)
}

export function trackAlertCreated() {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _safeTrack('alert_created', properties)
}

export function trackWebcheckCreated() {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _safeTrack('webcheck_created', properties)
}

export function trackConnectPopupViewed() {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _debugLog('trackConnectPopupViewed called', { bucketId, plan, currentBucket, properties })
  _safeTrack('connect_popup_viewed', properties)
}

export function trackPaywallViewed(featureName) {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {
    feature_name: featureName,
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _debugLog('trackPaywallViewed called', { featureName, bucketId, plan, currentBucket, properties })
  _safeTrack('paywall_viewed', properties)
}

export function trackGetFeatureClicked(featureName) {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {
    feature_name: featureName,
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _debugLog('trackGetFeatureClicked called', {
    featureName,
    bucketId,
    plan,
    currentBucket,
    properties,
  })
  _safeTrack('get_feature_clicked', properties)
}

export function trackContactUsClicked(featureName = null) {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (featureName) {
    properties.feature_name = featureName
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _debugLog('trackContactUsClicked called', {
    featureName,
    bucketId,
    plan,
    currentBucket,
    properties,
  })
  _safeTrack('contact_us_clicked', properties)
}

export function trackPurchase({ planName, planId, revenue, isTrial, interval, subscriptionId }) {
  const currentBucket = store.getters.currentBucket

  const properties = {
    plan_name: planName,
    revenue: revenue,
    plan_duration: interval,
  }

  if (subscriptionId) {
    properties.subscription_id = subscriptionId
  }

  if (isTrial) {
    properties.is_trial = true
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
  }

  _debugLog('trackPurchase called', {
    planName,
    planId,
    revenue,
    isTrial,
    interval,
    subscriptionId,
    properties,
  })
  _safeTrack('purchase', properties)

  _safePeopleSet({
    plan_name: planName,
    last_purchase_at: new Date().toISOString(),
  })
}

export function trackCheckoutAddCardViewed() {
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null
  const currentBucket = store.getters.currentBucket

  const properties = {}

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (plan) {
    properties.plan_name = plan.name
  }

  _debugLog('trackCheckoutAddCardViewed called', { bucketId, plan, currentBucket, properties })
  _safeTrack('checkout_add_card_viewed', properties)
}

export function trackSubscriptionCancelled({ planName, cancelReason }) {
  const currentBucket = store.getters.currentBucket
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null

  const properties = {
    plan_name: planName,
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (cancelReason) {
    properties.cancel_reason = cancelReason
  }

  _debugLog('trackSubscriptionCancelled called', { planName, cancelReason, properties })
  _safeTrack('subscription_cancelled', properties)
}

export function trackSubscriptionDowngrade({ planNameCurrent, planNamePrevious, cancelReason }) {
  const currentBucket = store.getters.currentBucket
  const bucketId = store.state.currentBucketId
  const plan = bucketId ? store.getters.bucketPlan(bucketId) : null

  const properties = {
    plan_name_current: planNameCurrent,
    plan_name_previous: planNamePrevious,
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
    properties.process_granted = plan ? plan.process_limit : 0
  }

  if (cancelReason) {
    properties.cancel_reason = cancelReason
  }

  _debugLog('trackSubscriptionDowngrade called', {
    planNameCurrent,
    planNamePrevious,
    cancelReason,
    properties,
  })
  _safeTrack('subscription_downgrade', properties)
}

export function trackSubscriptionUpgraded({
  planNameCurrent,
  planNamePrevious,
  revenue,
  interval,
}) {
  const currentBucket = store.getters.currentBucket

  const properties = {
    plan_name_current: planNameCurrent,
    plan_name_previous: planNamePrevious,
    revenue: revenue,
    plan_duration: interval,
  }

  if (currentBucket) {
    properties.process_tracked = currentBucket.processes.length || 0
  }

  _debugLog('trackSubscriptionUpgraded called', {
    planNameCurrent,
    planNamePrevious,
    revenue,
    interval,
    properties,
  })
  _safeTrack('subscription_upgraded', properties)

  _safePeopleSet({
    plan_name: planNameCurrent,
    last_purchase_at: new Date().toISOString(),
  })
}

export { mixpanel }
